import { useState } from 'react';
import { MapPin, Phone, Mail, Send, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Message sent! We will get back to you soon.');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-16" data-testid="contact-page">
      <section className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-3">Get in Touch</p>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white mb-4">Contact Us</h1>
          <p className="text-lg text-white/80 font-body">Have questions? We would love to hear from you.</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <div>
                <h3 className="font-heading font-bold text-navy text-lg mb-4">Contact Information</h3>
                <div className="space-y-4">
                  {[
                    { icon: MapPin, title: 'Address', text: 'Port St. Lucie, FL' },
                    { icon: Mail, title: 'Email', text: 'info@safeflorida.org' },
                    { icon: Phone, title: 'Phone', text: '(772) 555-SAFE' },
                    { icon: Clock, title: 'Hours', text: 'Mon-Fri 9AM - 5PM EST' },
                  ].map(({ icon: Icon, title, text }) => (
                    <div key={title} className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-navy/5 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-navy" />
                      </div>
                      <div>
                        <p className="text-sm font-body font-medium text-navy">{title}</p>
                        <p className="text-sm font-body text-gray-600">{text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-6 bg-navy/5 rounded-lg">
                <p className="font-signature text-gold text-2xl mb-1">F.C. Clark</p>
                <p className="text-xs text-gray-600 font-body">Founder & Leader, Safe Florida</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="border border-gray-200 rounded-lg p-8">
                <h3 className="font-heading font-bold text-navy text-lg mb-6">Send Us a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="contact-name" className="font-body text-sm text-gray-700">Name</Label>
                      <Input id="contact-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required className="mt-1 font-body" data-testid="contact-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="contact-email" className="font-body text-sm text-gray-700">Email</Label>
                      <Input id="contact-email" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required className="mt-1 font-body" data-testid="contact-email-input" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="contact-subject" className="font-body text-sm text-gray-700">Subject</Label>
                    <Input id="contact-subject" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} className="mt-1 font-body" data-testid="contact-subject-input" />
                  </div>
                  <div>
                    <Label htmlFor="contact-message" className="font-body text-sm text-gray-700">Message</Label>
                    <Textarea id="contact-message" value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} required rows={5} className="mt-1 font-body" data-testid="contact-message-input" />
                  </div>
                  <Button type="submit" disabled={loading} className="btn-primary-safe font-body font-bold" data-testid="contact-submit-btn">
                    <Send className="w-4 h-4 mr-2" /> {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
